<script lang="ts">
  import EggMark from '../lib/design/EggMark.svelte';
  import { session, ApiError, GOOGLE_START_PATH } from '../lib/session.svelte';

  // A reload mid-two-factor still has the pre-auth cookie: resume at the code
  // step rather than making the password be re-entered.
  let step = $state<'password' | 'totp'>(session.state.mfaPending ? 'totp' : 'password');
  let email = $state('');
  let password = $state('');
  let code = $state('');
  let busy = $state(false);
  let error = $state('');

  $effect(() => {
    if (session.state.mfaPending && step === 'password') step = 'totp';
  });

  // The Google callback bounces back to `/?oauth_error=<code>` on any failure.
  // Show it in the same slot as a password error, then strip the param.
  const OAUTH_ERRORS: Record<string, string> = {
    bad_state: 'That sign-in link expired. Try again.',
    exchange_failed: "Couldn't reach Google. Try again in a moment.",
    denied: 'Google sign-in was cancelled.',
    rate_limited: 'Too many sign-up attempts from your network — wait a bit.',
    needs_signin:
      'That email already has a Kip account. Sign in with your password, then connect Google from Settings.',
  };
  $effect(() => {
    const p = new URLSearchParams(location.search);
    const c = p.get('oauth_error');
    if (!c) return;
    error = OAUTH_ERRORS[c] ?? 'Google sign-in failed. Try again.';
    p.delete('oauth_error');
    history.replaceState({}, '', location.pathname + (p.toString() ? `?${p}` : ''));
  });

  async function submitPassword(e: SubmitEvent) {
    e.preventDefault();
    if (busy || !email.trim() || !password) return;
    busy = true;
    error = '';
    try {
      const needsTotp = await session.login(email.trim(), password);
      password = '';
      if (needsTotp) step = 'totp';
      // else: App unmounts this screen once session.ready flips.
    } catch (err) {
      if (err instanceof ApiError && err.code === 'unknown_account') {
        error = 'No account found for that email — Kip is invite-only. Ask your operator for an invite.';
        return;
      }
      error = err instanceof ApiError ? err.message : 'Something went wrong. Try again.';
    } finally {
      busy = false;
    }
  }

  async function submitTotp(e: SubmitEvent) {
    e.preventDefault();
    if (busy || code.trim().length < 6) return;
    busy = true;
    error = '';
    try {
      await session.submitTotp(code.trim());
    } catch (err) {
      error =
        err instanceof ApiError ? err.message : 'That code did not work. Try the current one.';
      code = '';
    } finally {
      busy = false;
    }
  }

  async function back() {
    // drop the half-finished (mfa-pending) session so the effect doesn't
    // bounce straight back to the code step
    await session.logout();
    step = 'password';
    code = '';
    email = '';
    error = '';
  }
</script>

<div class="connect">
  <div class="brand">
    <EggMark size={44} />
    <h1>Kip</h1>
    <p class="sub">Reach your coop from anywhere.</p>
  </div>

  {#if step === 'password'}
    <form onsubmit={submitPassword}>
      <label>
        <span class="eyebrow">Email</span>
        <input
          type="email"
          autocomplete="username"
          inputmode="email"
          bind:value={email}
          required
          autocapitalize="none"
          spellcheck="false"
        />
      </label>
      <label>
        <span class="eyebrow">Password</span>
        <input type="password" autocomplete="current-password" bind:value={password} required />
      </label>
      {#if error}<p class="error" role="alert">{error}</p>{/if}
      <button class="go" type="submit" disabled={busy || !email.trim() || !password}>
        {busy ? 'Signing in…' : 'Sign in'}
      </button>
    </form>
    {#if session.state.oauth?.google}
      <div class="divider"><span>or</span></div>
      <a class="oauth" href={GOOGLE_START_PATH}>
        <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
          <path
            fill="#4285F4"
            d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z"
          />
          <path
            fill="#34A853"
            d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.92v2.33A9 9 0 0 0 9 18Z"
          />
          <path
            fill="#FBBC05"
            d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.92a9 9 0 0 0 0 8.1l3.05-2.33Z"
          />
          <path
            fill="#EA4335"
            d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.47.9 11.43 0 9 0A9 9 0 0 0 .92 4.95L3.97 7.3C4.68 5.16 6.66 3.58 9 3.58Z"
          />
        </svg>
        Continue with Google
      </a>
    {/if}
    <p class="foot">Use your Kip account — the same login as the customer portal.</p>
    <p class="foot">New here? Kip is invite-only — ask your operator for an invite.</p>
  {:else}
    <p class="foot">
      Two-factor is on{session.state.email ? ` for ${session.state.email}` : ''} — enter the code from
      your authenticator app.
    </p>
    <form onsubmit={submitTotp}>
      <label>
        <span class="eyebrow">Authentication code</span>
        <input
          type="text"
          inputmode="numeric"
          autocomplete="one-time-code"
          bind:value={code}
          maxlength="6"
          pattern="[0-9]*"
          placeholder="000000"
          class="totp"
        />
      </label>
      {#if error}<p class="error" role="alert">{error}</p>{/if}
      <button class="go" type="submit" disabled={busy || code.trim().length < 6}>
        {busy ? 'Checking…' : 'Verify'}
      </button>
    </form>
    <button class="link eyebrow" type="button" onclick={back}>← Use a different account</button>
  {/if}
</div>

<style>
  .connect {
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 100dvh;
    max-width: 360px;
    margin: 0 auto;
    padding: 32px 24px calc(32px + env(safe-area-inset-bottom));
    gap: 28px;
  }
  .brand {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 8px;
  }
  h1 {
    font-family: var(--serif);
    font-size: 34px;
    letter-spacing: -0.02em;
  }
  .sub {
    color: var(--ink-soft);
    font-size: 14px;
  }
  form {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  label {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  input {
    border: 1px solid var(--line);
    border-radius: 12px;
    padding: 12px 14px;
    background: var(--cream-2);
    font-size: 16px; /* keeps iOS from zooming on focus */
  }
  input:focus-visible {
    outline: 2px solid var(--rooster);
    outline-offset: 1px;
  }
  .totp {
    font-family: var(--mono);
    letter-spacing: 0.4em;
    text-align: center;
    font-size: 22px;
  }
  .go {
    margin-top: 4px;
    border-radius: 12px;
    padding: 13px 20px;
    background: var(--rooster);
    color: var(--cream);
    font-family: var(--mono);
    font-weight: 700;
    font-size: 13px;
    letter-spacing: 0.03em;
  }
  .go:disabled {
    opacity: 0.4;
  }
  .error {
    color: var(--rooster-deep);
    font-size: 13px;
  }
  .divider {
    display: flex;
    align-items: center;
    gap: 10px;
    color: var(--ink-faint);
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }
  .divider::before,
  .divider::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--line);
  }
  .oauth {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    border: 1px solid var(--line);
    border-radius: 12px;
    padding: 12px 20px;
    background: var(--cream-2);
    color: var(--ink);
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
  }
  .oauth:active {
    background: var(--cream);
  }
  .foot {
    text-align: center;
    color: var(--ink-faint);
    font-size: 12px;
  }
  .link {
    align-self: center;
    color: var(--ink-soft);
  }
</style>
